import React, { useState, useEffect } from "react";
import { generateFacultyActivity, uploadFiles } from "../api";
import { C as TOKENS } from "../constants";
import PageHeader from "../components/PageHeader";
import { getPlannerContext, plannerContextBanner } from "../utils/plannerContext";

// ── Download as plain text ────────────────────────────────────────────────────
function downloadActivityAsText(subject, topic, activity) {
  const lines = [];
  lines.push(`FACULTY ACTIVITY PLAN — ${subject} | ${topic}`);
  lines.push("=".repeat(60));
  if (activity.title)    lines.push("Activity: " + activity.title);
  if (activity.type)     lines.push("Type: "     + activity.type);
  if (activity.duration) lines.push("Duration: " + activity.duration + " minutes");
  if (activity.objective)      lines.push("\nObjective:\n" + activity.objective);
  if (activity.prerequisites?.length)
    lines.push("\nPrerequisites:\n" + activity.prerequisites.map(p => "• " + p).join("\n"));
  if (activity.learningOutcome) lines.push("\nLearning Outcome:\n" + activity.learningOutcome);

  if (activity.steps?.length) {
    lines.push("\nStep-by-Step Instructions:");
    activity.steps.forEach(s => {
      lines.push(`\nStep ${s.step} (${s.time}):`);
      lines.push("  " + s.instruction);
      if (s.facultyRole) lines.push("  Faculty: " + s.facultyRole);
      if (s.studentRole) lines.push("  Students: " + s.studentRole);
      if (s.facultyNotes) {
        lines.push("  [FACULTY NOTES]");
        if (s.facultyNotes.conceptToExplain)     lines.push("  Concept: "    + s.facultyNotes.conceptToExplain);
        if (s.facultyNotes.boardWork)            lines.push("  Board Work: " + s.facultyNotes.boardWork);
        if (s.facultyNotes.analogyOrExample)     lines.push("  Analogy: "    + s.facultyNotes.analogyOrExample);
        if (s.facultyNotes.keyPointsToEmphasize?.length)
          lines.push("  Key Points:\n" + s.facultyNotes.keyPointsToEmphasize.map(k => "    • " + k).join("\n"));
        if (s.facultyNotes.anticipatedQuestions?.length)
          s.facultyNotes.anticipatedQuestions.forEach(q => lines.push(`  Q: ${q.question}\n  A: ${q.answer}`));
        if (s.facultyNotes.transitionToNext) lines.push("  Transition: " + s.facultyNotes.transitionToNext);
      }
    });
  }

  if (activity.materials?.length)
    lines.push("\nMaterials Needed:\n" + activity.materials.map(m => "• " + m).join("\n"));

  if (activity.assessmentRubric?.length) {
    lines.push("\nAssessment Rubric:");
    activity.assessmentRubric.forEach(r =>
      lines.push(`  ${r.criterion}: Excellent — ${r.excellent} | Satisfactory — ${r.satisfactory} | Needs Work — ${r.needsWork}`)
    );
  }

  if (activity.worksheet) {
    lines.push("\nStudent Worksheet:");
    activity.worksheet.questions?.forEach((q, i) => {
      lines.push(`\nQ${i + 1} [${q.type}, ${q.marks} marks]: ${q.question}`);
      if (q.modelAnswer)  lines.push("  Answer: " + q.modelAnswer);
      if (q.gradingHints) lines.push("  Grading: " + q.gradingHints);
    });
    activity.worksheet.practiceProblems?.forEach((p, i) => {
      lines.push(`\nPractice ${i + 1}: ${p.problem}`);
      if (p.solution) lines.push("  Solution: " + p.solution);
      p.stepByStepSolution?.forEach(step => lines.push("    → " + step));
    });
  }

  const di = activity.differentiatedInstruction;
  if (di) {
    lines.push("\nDifferentiated Instruction:");
    if (di.emergingLearners) {
      lines.push("\n🔵 Emerging Learners:");
      di.emergingLearners.modifications?.forEach(m => lines.push("  • " + m));
      di.emergingLearners.scaffoldingStrategies?.forEach(s => lines.push("  • " + s));
      di.emergingLearners.simplifiedTasks?.forEach(t => lines.push("  • " + t));
      if (di.emergingLearners.additionalSupport) lines.push("  Support: " + di.emergingLearners.additionalSupport);
    }
    if (di.gradeLevelLearners) {
      lines.push("\n🟢 Grade Level Learners:");
      di.gradeLevelLearners.coreActivities?.forEach(a => lines.push("  • " + a));
      di.gradeLevelLearners.reinforcementStrategies?.forEach(s => lines.push("  • " + s));
      if (di.gradeLevelLearners.checkForUnderstanding) lines.push("  Check: " + di.gradeLevelLearners.checkForUnderstanding);
    }
    if (di.advancedLearners) {
      lines.push("\n🔴 Advanced Learners:");
      di.advancedLearners.extensionTasks?.forEach(t => lines.push("  • " + t));
      di.advancedLearners.higherOrderQuestions?.forEach(q => lines.push("  • " + q));
      if (di.advancedLearners.connectionToAdvancedTopics) lines.push("  Advanced: " + di.advancedLearners.connectionToAdvancedTopics);
      if (di.advancedLearners.leadershipOpportunities)   lines.push("  Leadership: " + di.advancedLearners.leadershipOpportunities);
    }
  }

  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${subject}_${topic}_activity.txt`.replace(/[^a-zA-Z0-9._-]/g, "_");
  a.click();
  URL.revokeObjectURL(url);
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  primary:   TOKENS.primary,
  bg:        TOKENS.bg,
  surface:   TOKENS.surface,
  border:    TOKENS.border,
  error:     TOKENS.error,
  errorBg:   TOKENS.errorBg,
  success:   TOKENS.success,
  successBg: TOKENS.successBg,
  muted:     TOKENS.muted,
  text:      TOKENS.text,
};

// ── Shared card wrapper ───────────────────────────────────────────────────────
function Card({ title, children, color = C.primary, bg = "#E8F0FF" }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: bg, borderBottom: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14, color }}>
        {title}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

// ── Simple label + string/array field ────────────────────────────────────────
function Field({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      {label && (
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
          {label}
        </div>
      )}
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
        {Array.isArray(value)
          ? value.map((v, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3 }}>
              <span style={{ color: C.primary, fontWeight: 700, minWidth: 14 }}>•</span>
              <span>{typeof v === "string" ? v : JSON.stringify(v)}</span>
            </div>
          ))
          : <span style={{ whiteSpace: "pre-wrap" }}>{String(value)}</span>
        }
      </div>
    </div>
  );
}

// ── Section header inside a card ─────────────────────────────────────────────
function SectionHeader({ children, color = C.primary }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: "uppercase",
      letterSpacing: "0.06em", marginBottom: 8, marginTop: 4, borderBottom: `1px solid ${C.border}`, paddingBottom: 4 }}>
      {children}
    </div>
  );
}

// ── Pill badge ────────────────────────────────────────────────────────────────
function Pill({ children, color = C.primary, bg = "#E8F0FF" }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 20,
      background: bg, color, fontSize: 11, fontWeight: 700, marginRight: 6, marginBottom: 4 }}>
      {children}
    </span>
  );
}

// ── Step card with expandable faculty notes ───────────────────────────────────
function StepCard({ step, index, total }) {
  const [expanded, setExpanded] = useState(false);
  const fn = step.facultyNotes;

  return (
    <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: index < total - 1 ? `1px solid ${C.border}` : "none" }}>
      {/* Step header row */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ minWidth: 28, height: 28, background: C.primary, color: "#fff", borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
          {step.step || index + 1}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>⏱ {step.time}</div>
          <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 4 }}>{step.instruction}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: fn ? 6 : 0 }}>
            {step.facultyRole && (
              <span style={{ fontSize: 12, color: "#0B5FFF", background: "#E8F0FF", padding: "2px 8px", borderRadius: 6 }}>
                👩‍🏫 {step.facultyRole}
              </span>
            )}
            {step.studentRole && (
              <span style={{ fontSize: 12, color: "#059669", background: "#ECFDF5", padding: "2px 8px", borderRadius: 6 }}>
                👨‍🎓 {step.studentRole}
              </span>
            )}
          </div>

          {/* Faculty Notes toggle */}
          {fn && (
            <button
              onClick={() => setExpanded(e => !e)}
              style={{ fontSize: 11, padding: "4px 12px", background: expanded ? "#0B5FFF" : "#E8F0FF",
                color: expanded ? "#fff" : "#0B5FFF", border: "1px solid #3D7EFF",
                borderRadius: 6, cursor: "pointer", fontWeight: 600, marginTop: 4 }}>
              {expanded ? "▲ Hide Faculty Notes" : "▼ Show Faculty Notes"}
            </button>
          )}
        </div>
      </div>

      {/* Expanded faculty notes panel */}
      {fn && expanded && (
        <div style={{ marginTop: 10, marginLeft: 40, background: "#FAF5FF",
          border: "1px solid #DDD6FE", borderRadius: 8, padding: 14 }}>

          {fn.conceptToExplain && (
            <div style={{ marginBottom: 10 }}>
              <SectionHeader color="#0B5FFF">📖 Concept to Explain</SectionHeader>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.75, margin: 0 }}>{fn.conceptToExplain}</p>
            </div>
          )}

          {fn.keyPointsToEmphasize?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <SectionHeader color="#0B5FFF">🔑 Key Points to Emphasize</SectionHeader>
              {fn.keyPointsToEmphasize.map((pt, i) => (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 3, fontSize: 13 }}>
                  <span style={{ color: "#0B5FFF", fontWeight: 700 }}>•</span>
                  <span>{pt}</span>
                </div>
              ))}
            </div>
          )}

          {fn.boardWork && (
            <div style={{ marginBottom: 10 }}>
              <SectionHeader color="#0B5FFF">🖊 Board Work</SectionHeader>
              <div style={{ fontSize: 13, color: C.text, background: "#fff",
                border: "1px dashed #3D7EFF", borderRadius: 6, padding: "8px 12px", whiteSpace: "pre-wrap" }}>
                {fn.boardWork}
              </div>
            </div>
          )}

          {fn.analogyOrExample && (
            <div style={{ marginBottom: 10 }}>
              <SectionHeader color="#0B5FFF">💡 Analogy / Example</SectionHeader>
              <div style={{ fontSize: 13, color: "#4B5563", fontStyle: "italic", lineHeight: 1.7 }}>{fn.analogyOrExample}</div>
            </div>
          )}

          {fn.anticipatedQuestions?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <SectionHeader color="#0B5FFF">❓ Anticipated Student Questions</SectionHeader>
              {fn.anticipatedQuestions.map((qa, i) => (
                <div key={i} style={{ marginBottom: 8, background: "#fff",
                  border: "1px solid #DDD6FE", borderRadius: 6, padding: "8px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#0B5FFF", marginBottom: 2 }}>Q: {qa.question}</div>
                  <div style={{ fontSize: 12, color: C.text }}>A: {qa.answer}</div>
                </div>
              ))}
            </div>
          )}

          {fn.transitionToNext && (
            <div>
              <SectionHeader color="#0B5FFF">➡ Transition to Next Step</SectionHeader>
              <div style={{ fontSize: 13, color: "#6D28D9", fontStyle: "italic", background: "#fff",
                border: "1px solid #DDD6FE", borderRadius: 6, padding: "8px 12px" }}>
                "{fn.transitionToNext}"
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Worksheet renderer ────────────────────────────────────────────────────────
function WorksheetSection({ worksheet }) {
  const [showAnswers, setShowAnswers] = useState(false);

  if (!worksheet) return null;
  const { questions = [], practiceProblems = [] } = worksheet;
  if (!questions.length && !practiceProblems.length) return null;

  return (
    <Card title="📝 Student Worksheet" color="#0B5FFF" bg="#E8F0FF">
      {/* Toggle answers */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button
          onClick={() => setShowAnswers(a => !a)}
          style={{ fontSize: 12, padding: "6px 14px", background: showAnswers ? "#0B5FFF" : "#E8F0FF",
            color: showAnswers ? "#fff" : "#0B5FFF", border: "1px solid #3D7EFF",
            borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
          {showAnswers ? "🙈 Hide Answers" : "👁 Show Model Answers"}
        </button>
      </div>

      {/* Questions */}
      {questions.length > 0 && (
        <>
          <SectionHeader color="#0B5FFF">Questions</SectionHeader>
          {questions.map((q, i) => (
            <div key={i} style={{ marginBottom: 14, background: "#fff",
              border: "1px solid #DDD6FE", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1 }}>
                  Q{q.questionNumber || i + 1}. {q.question}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {q.type  && <Pill color="#0B5FFF" bg="#E8F0FF">{q.type}</Pill>}
                  {q.marks && <Pill color="#059669" bg="#ECFDF5">{q.marks} marks</Pill>}
                </div>
              </div>

              {showAnswers && (
                <div style={{ marginTop: 10 }}>
                  {q.modelAnswer && (
                    <div style={{ background: "#ECFDF5", border: "1px solid #6EE7B7",
                      borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#059669",
                        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Model Answer</div>
                      <div style={{ fontSize: 13, color: "#065F46", whiteSpace: "pre-wrap" }}>{q.modelAnswer}</div>
                    </div>
                  )}
                  {q.gradingHints && (
                    <div style={{ background: "#E7F0F6", border: "1px solid #7BA7C9",
                      borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#1B4F72",
                        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Grading Hints</div>
                      <div style={{ fontSize: 12, color: "#143B56" }}>{q.gradingHints}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Practice Problems */}
      {practiceProblems.length > 0 && (
        <>
          <SectionHeader color="#0B5FFF">Practice Problems</SectionHeader>
          {practiceProblems.map((p, i) => (
            <div key={i} style={{ marginBottom: 14, background: "#fff",
              border: "1px solid #DDD6FE", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: showAnswers ? 8 : 0 }}>
                P{i + 1}. {p.problem}
              </div>
              {showAnswers && (
                <div>
                  {p.solution && (
                    <div style={{ background: "#ECFDF5", border: "1px solid #6EE7B7",
                      borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#059669",
                        textTransform: "uppercase", marginBottom: 4 }}>Solution</div>
                      <div style={{ fontSize: 13, color: "#065F46" }}>{p.solution}</div>
                    </div>
                  )}
                  {p.stepByStepSolution?.length > 0 && (
                    <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE",
                      borderRadius: 6, padding: "8px 12px" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#1D4ED8",
                        textTransform: "uppercase", marginBottom: 6 }}>Step-by-Step</div>
                      {p.stepByStepSolution.map((step, j) => (
                        <div key={j} style={{ display: "flex", gap: 8, marginBottom: 4, fontSize: 12, color: "#1E40AF" }}>
                          <span style={{ fontWeight: 700, minWidth: 20 }}>{j + 1}.</span>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

// ── Differentiated Instruction renderer ──────────────────────────────────────
function DifferentiatedSection({ di }) {
  if (!di) return null;

  const tiers = [
    {
      key:   "emergingLearners",
      label: "🔵 Emerging Learners",
      desc:  di.emergingLearners?.description,
      color: "#1D4ED8",
      bg:    "#EFF6FF",
      border:"#BFDBFE",
      rows: [
        { label: "Modifications",          items: di.emergingLearners?.modifications },
        { label: "Scaffolding Strategies", items: di.emergingLearners?.scaffoldingStrategies },
        { label: "Simplified Tasks",       items: di.emergingLearners?.simplifiedTasks },
        { label: "Additional Support",     text:  di.emergingLearners?.additionalSupport },
      ],
    },
    {
      key:   "gradeLevelLearners",
      label: "🟢 Grade Level Learners",
      desc:  di.gradeLevelLearners?.description,
      color: "#059669",
      bg:    "#ECFDF5",
      border:"#6EE7B7",
      rows: [
        { label: "Core Activities",            items: di.gradeLevelLearners?.coreActivities },
        { label: "Reinforcement Strategies",   items: di.gradeLevelLearners?.reinforcementStrategies },
        { label: "Check for Understanding",    text:  di.gradeLevelLearners?.checkForUnderstanding },
      ],
    },
    {
      key:   "advancedLearners",
      label: "🔴 Advanced Learners",
      desc:  di.advancedLearners?.description,
      color: "#DC2626",
      bg:    "#FEF2F2",
      border:"#FECACA",
      rows: [
        { label: "Extension Tasks",              items: di.advancedLearners?.extensionTasks },
        { label: "Higher Order Questions",       items: di.advancedLearners?.higherOrderQuestions },
        { label: "Connection to Advanced Topics",text:  di.advancedLearners?.connectionToAdvancedTopics },
        { label: "Leadership Opportunities",     text:  di.advancedLearners?.leadershipOpportunities },
      ],
    },
  ];

  const hasAny = tiers.some(t => di[t.key]);
  if (!hasAny) return null;

  return (
    <Card title="♿ Differentiated Instruction" color="#0284C7" bg="#F0F9FF">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {tiers.map(tier => {
          const data = di[tier.key];
          if (!data) return null;
          const hasContent = tier.rows.some(r => (r.items?.length > 0) || r.text);
          if (!hasContent && !tier.desc) return null;

          return (
            <div key={tier.key} style={{ background: tier.bg, border: `1px solid ${tier.border}`,
              borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: tier.color, marginBottom: 6 }}>
                {tier.label}
              </div>
              {tier.desc && (
                <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginBottom: 8 }}>
                  {tier.desc}
                </div>
              )}
              {tier.rows.map((row, ri) => {
                if (row.items?.length > 0) {
                  return (
                    <div key={ri} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: tier.color,
                        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                        {row.label}
                      </div>
                      {row.items.map((item, ii) => (
                        <div key={ii} style={{ display: "flex", gap: 6, marginBottom: 3, fontSize: 13, color: C.text }}>
                          <span style={{ color: tier.color, fontWeight: 700 }}>•</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                if (row.text) {
                  return (
                    <div key={ri} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: tier.color,
                        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
                        {row.label}
                      </div>
                      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{row.text}</div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FacultyAssistant() {
  const [subject,         setSubject]         = useState("");
  const [topic,           setTopic]           = useState("");
  const [duration,        setDuration]        = useState(45);
  const [learningOutcome, setLearningOutcome] = useState("");
  const [syllabusText,    setSyllabusText]    = useState("");
  const [loading,         setLoading]         = useState(false);
  const [activity,        setActivity]        = useState(null);
  const [error,           setError]           = useState("");
  const [uploading,       setUploading]       = useState(false);
  const [history,         setHistory]         = useState([]);
  const [showHistory,     setShowHistory]     = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("facultyActivityHistory") || "[]");
      setHistory(saved);
    } catch {}
    const ctx = getPlannerContext();
    setSubject((s) => s || ctx.subject || "");
    setTopic((t) => t || ctx.topicHint || "");
    setSyllabusText((sy) => sy || [ctx.syllabus, ctx.dsaSyllabus].filter(Boolean).join("\n\n"));
    setLearningOutcome((o) =>
      o || (ctx.problem ? `Connect class activity to the student project: ${ctx.problem.slice(0, 180)}` : "")
    );
  }, []);

  const saveToHistory = (sub, top, act) => {
    const entry   = { subject: sub, topic: top, activity: act, savedAt: new Date().toISOString() };
    const updated = [entry, ...history.filter(h => !(h.subject === sub && h.topic === top))].slice(0, 20);
    setHistory(updated);
    try { localStorage.setItem("facultyActivityHistory", JSON.stringify(updated)); } catch {}
  };

  const deleteHistoryItem = (sub, top) => {
    if (!window.confirm(`Delete saved activity for "${sub}" – "${top}"?`)) return;
    const updated = history.filter(h => !(h.subject === sub && h.topic === top));
    setHistory(updated);
    localStorage.setItem("facultyActivityHistory", JSON.stringify(updated));
  };

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const res  = await uploadFiles({ syllabus: Array.from(files) });
      const text = (res.extracted?.syllabus || []).map(f => f.content).join("\n");
      setSyllabusText(text);
    } catch {}
    setUploading(false);
  };

  const handleGenerate = async () => {
    if (!subject.trim() || !topic.trim()) { setError("Subject and topic are required"); return; }
    setLoading(true); setError(""); setActivity(null);
    try {
      const res = await generateFacultyActivity(subject, topic, duration, learningOutcome, syllabusText);
      if (res.success) {
        const act = res.activity?.activity || res.activity;
        setActivity(act);
        saveToHistory(subject, topic, act);
      } else setError(res.error || "Generation failed");
    } catch (err) { setError(err.response?.data?.error || "Generation failed"); }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "8px 4px 28px" }}>
      <PageHeader
        title="Faculty Assistant"
        subtitle="Generate a full activity plan with steps, faculty notes, rubric, worksheet answers, and differentiated instruction."
        icon="🤖"
        actions={(
          <button type="button" onClick={() => setShowHistory(!showHistory)}
            style={{ fontSize: 12, padding: "7px 14px", background: showHistory ? C.primary : "#E8F0FF",
              color: showHistory ? "#fff" : C.primary, border: "1px solid #3D7EFF",
              borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>
            Saved ({history.length})
          </button>
        )}
      />

      <div style={{
        marginBottom: 14, padding: "10px 14px", borderRadius: 10,
        background: "#E8F0FF", border: "1px solid #3D7EFF33",
        fontSize: 12.5, color: C.primary, fontWeight: 600,
      }}>
        {plannerContextBanner(getPlannerContext())}
      </div>

      {/* History Panel */}
      {showHistory && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 10 }}>Previously Generated Activities</div>
          {history.length === 0
            ? <div style={{ fontSize: 13, color: C.muted }}>No saved activities yet.</div>
            : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {history.map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "10px 14px", background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{item.subject}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{item.topic} · {new Date(item.savedAt).toLocaleDateString()}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setSubject(item.subject); setTopic(item.topic); setActivity(item.activity); setShowHistory(false); }}
                        style={{ fontSize: 12, padding: "6px 14px", background: C.primary, color: "#fff",
                          border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        Load
                      </button>
                      <button onClick={() => downloadActivityAsText(item.subject, item.topic, item.activity)}
                        style={{ fontSize: 12, padding: "6px 14px", background: C.success, color: "#fff",
                          border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        ⬇ .txt
                      </button>
                      <button onClick={() => deleteHistoryItem(item.subject, item.topic)}
                        style={{ fontSize: 12, padding: "6px 14px", background: C.error, color: "#fff",
                          border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* Input Form */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Subject *</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Computer Networks"
              style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Topic *</label>
            <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. OSI Model, TCP/IP Handshake"
              style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 14, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Duration (minutes)</label>
            <input type="number" min={10} max={180} value={duration} onChange={e => setDuration(Number(e.target.value))}
              style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
              Desired Learning Outcome <span style={{ fontWeight: 400, color: C.muted }}>(optional)</span>
            </label>
            <input value={learningOutcome} onChange={e => setLearningOutcome(e.target.value)}
              placeholder="e.g. Students can explain the 3-way handshake with a diagram"
              style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: "border-box" }} />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>
            Syllabus Upload <span style={{ fontWeight: 400, color: C.muted }}>(optional — for topic-specific output)</span>
          </label>
          <label style={{ display: "inline-block", cursor: "pointer", padding: "9px 16px", background: "#E8F0FF",
            color: C.primary, border: "1px solid #3D7EFF", borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
            {uploading ? "Uploading..." : syllabusText ? "✓ Syllabus Loaded" : "📎 Upload Syllabus"}
            <input type="file" accept=".pdf,.txt,.docx" style={{ display: "none" }} onChange={e => handleUpload(e.target.files)} />
          </label>
        </div>

        {error && (
          <div style={{ padding: "8px 12px", background: C.errorBg, color: C.error, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <button onClick={handleGenerate} disabled={loading}
          style={{ background: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "11px 24px",
            fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? "✨ Generating activity plan..." : "✨ Generate Activity Plan"}
        </button>
      </div>

      {/* ── Activity Result ── */}
      {activity && (
        <div>
          {/* Title bar */}
          <div style={{ background: C.successBg, border: "1px solid #6EE7B7", borderRadius: 10,
            padding: "12px 16px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.success }}>{activity.title || `Activity: ${topic}`}</div>
              <div style={{ fontSize: 13, color: "#065F46", marginTop: 3 }}>
                Type: {activity.type} · Duration: {activity.duration} min
              </div>
            </div>
            <button onClick={() => downloadActivityAsText(subject, topic, activity)}
              style={{ fontSize: 12, padding: "7px 16px", background: C.success, color: "#fff",
                border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
              ⬇ Download .txt
            </button>
          </div>

          {/* Objective */}
          <Card title="🎯 Objective & Outcome" color="#059669" bg="#ECFDF5">
            <Field label="Objective"        value={activity.objective} />
            <Field label="Prerequisites"    value={activity.prerequisites} />
            <Field label="Learning Outcome" value={activity.learningOutcome} />
          </Card>

          {/* Steps with faculty notes */}
          <Card title="📋 Step-by-Step Instructions" color={C.primary} bg="#E8F0FF">
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Click <strong>Show Faculty Notes</strong> on any step to see detailed teaching content, board work, analogies, and anticipated questions.
            </div>
            {(activity.steps || []).map((step, i) => (
              <StepCard key={i} step={step} index={i} total={activity.steps.length} />
            ))}
          </Card>

          {/* Materials */}
          {activity.materials?.length > 0 && (
            <Card title="📦 Materials Needed" color="#1B4F72" bg="#E7F0F6">
              <Field label="" value={activity.materials} />
            </Card>
          )}

          {/* Rubric */}
          {activity.assessmentRubric?.length > 0 && (
            <Card title="📊 Assessment Rubric" color="#DC2626" bg="#FEF2F2">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: C.bg }}>
                      {["Criterion", "Excellent", "Satisfactory", "Needs Work"].map(h => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: "left", border: `1px solid ${C.border}`, fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activity.assessmentRubric.map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, fontWeight: 600 }}>{r.criterion}</td>
                        <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, color: C.success }}>{r.excellent}</td>
                        <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, color: "#1B4F72" }}>{r.satisfactory}</td>
                        <td style={{ padding: "8px 10px", border: `1px solid ${C.border}`, color: C.error }}>{r.needsWork}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Worksheet with answers */}
          <WorksheetSection worksheet={activity.worksheet} />

          {/* Differentiated Instruction — 3 tiers */}
          <DifferentiatedSection di={activity.differentiatedInstruction} />
        </div>
      )}
    </div>
  );
}